import { SyntheticEvent, useCallback, useContext, useMemo } from "react";
import { v4 as uuid } from "uuid";

import { ViewStateContext } from "@is/contexts";
import { useContentItems } from "@is/hooks";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import {
  ContentItem,
  ContentItemType,
  MediaOrderingRule,
  ProductItem,
  WidgetType,
} from "@is/types";
import {
  createWidgetItem,
  getMainImageUrl,
  getStylePriceRange,
  sortProductMedia,
} from "@is/utils";

type Props = {
  item: ProductItem;
  sectionId: string;
  contentItemId: string;
};

const useManageProductRowItem = ({ item, contentItemId, sectionId }: Props) => {
  const { sections, updateViewState } = useContext(ViewStateContext).content;

  const { handleOpenProductDetails } = useContentItems();

  const mediaUrl = useMemo(() => getMainImageUrl(item.media), [item]);

  const colorVariantsThumbnails = useMemo(() => {
    if (!item?.otherVariants) {
      return [];
    }

    return item.otherVariants.map((variant) =>
      getMainImageUrl(variant.media, MediaOrderingRule.ColorThumbnails)
    );
  }, [item]);

  const colorVariantsTotalItems = useMemo(() => {
    if (!item?.otherVariants) {
      return 0;
    }

    return item.otherVariants.length;
  }, [item]);

  const price = useMemo(() => {
    const priceRange = getStylePriceRange(item?.sizeInfos);

    return priceRange ?? item?.price;
  }, [item]);

  const handleClick = useCallback(async () => {
    await handleOpenProductDetails({
      productItem: item,
      sectionId,
      contentItemId,
      colorVariantId: item.id,
    });

    trackEvent({
      eventType: TrackingEventTypes.SelectColorVariant,
      data: {
        variantId: item.id,
        styleId: item.styleId,
      },
    });
  }, [contentItemId, handleOpenProductDetails, item, sectionId]);

  const handleAllColorsClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation();
      if (!item?.otherVariants) {
        return;
      }

      const otherVariants = [
        {
          color: item.color,
          media: sortProductMedia(item.media, MediaOrderingRule.MediaSlider),
          id: item.id,
          name: item.name,
          price: item.price,
          sizeInfos: item.sizeInfos,
          styleId: item.styleId,
        },
        ...item.otherVariants,
      ];

      const widgetItem = createWidgetItem({
        type: WidgetType.AllColors,
        items: otherVariants,
        attributes: { customSubtitle: item.name },
      });

      const contentItem: ContentItem = {
        type: ContentItemType.ContentWidgetItem,
        content: widgetItem,
        id: uuid(),
      };

      const currentSection = sections.find(
        (section) => section.id === sectionId
      );

      const itemId = item.id;

      const rowItemIndex =
        currentSection?.items.findIndex((item) =>
          Array.isArray(item.content)
            ? item.content.some((content) => content.id === itemId)
            : item.id === itemId
        ) + 1;

      const newSections = sections.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            items: [
              ...section.items.slice(0, rowItemIndex),
              contentItem,
              ...section.items.slice(rowItemIndex),
            ],
          };
        }
        return section;
      });

      updateViewState({
        sections: newSections,
        showSearchAndFooterDrawers: false,
      });

      trackEvent({
        eventType: TrackingEventTypes.OpenAllColorsWidget,
        data: {
          variantId: item.id,
          styleId: item.styleId,
        },
      });
    },
    [item, sectionId, sections, updateViewState]
  );

  return {
    mediaUrl,
    colorVariantsThumbnails,
    colorVariantsTotalItems,
    price,
    handleClick,
    handleAllColorsClick,
  };
};

export default useManageProductRowItem;
